import { Timestamp } from "firebase/firestore";

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const NUM = new Intl.NumberFormat("pt-BR");
const PCT = new Intl.NumberFormat("pt-BR", { style: "percent", maximumFractionDigits: 1 });

export function formatCurrency(value: number | null | undefined): string {
  return BRL.format(Number(value ?? 0));
}

export function formatNumber(value: number | null | undefined): string {
  return NUM.format(Number(value ?? 0));
}

/** "1 arquivo", "2 arquivos": o número com a palavra no singular ou no plural. */
export function formatCount(value: number, singular: string, plural: string): string {
  return `${formatNumber(value)} ${value === 1 ? singular : plural}`;
}

export function formatPercent(fraction: number | null | undefined): string {
  return PCT.format(Number(fraction ?? 0));
}

/**
 * O Firestore devolve data em três formas ao longo do projeto: `Timestamp`,
 * o objeto cru `{seconds, nanoseconds}` (quando o documento veio de um cache
 * ou de um JSON) e string ISO. Uma função só converte as três, para nenhuma
 * tela precisar adivinhar qual chegou.
 */
export function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "object" && value !== null && "seconds" in value) {
    const seconds = (value as { seconds: number }).seconds;
    return typeof seconds === "number" ? new Date(seconds * 1000) : null;
  }
  return null;
}

export function formatDate(value: unknown): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString("pt-BR") : "-";
}

export function formatDateTime(value: unknown): string {
  const d = toDate(value);
  if (!d) return "-";
  return `${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

export function formatMonthYear(month: number, year: number): string {
  const nomes = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  ];
  return `${nomes[month - 1] ?? month} de ${year}`;
}

export function formatPhone(raw: string | null | undefined): string {
  const digits = (raw ?? "").replace(/\D/g, "");
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return raw ?? "-";
}

/**
 * CNPJ sem máscara e em maiúsculas. Desde julho de 2026 o CNPJ pode ter letras
 * nas 12 primeiras posições (alfanumérico), então tirar tudo que não é dígito
 * apagaria parte dele.
 */
export function normalizeCnpj(raw: string | null | undefined): string {
  return (raw ?? "").toUpperCase().replace(/[^0-9A-Z]/g, "");
}

export function formatCnpj(raw: string | null | undefined): string {
  const d = normalizeCnpj(raw);
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(d)) return raw || "-";
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Remove acento e caixa, para a busca da tela casar "Pao" com "Pão". */
export function normalize(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

export function matches(haystack: string | undefined | null, needle: string): boolean {
  if (!needle) return true;
  return normalize(haystack ?? "").includes(normalize(needle));
}
